import { FC, useMemo } from 'react';
import usePermissionsKeys from 'app/hooks/usePermissionsKeys';
import MoreMenuTooltip from 'components/navigation/verticalNavigation/mainMenu/MoreMenuTooltip';
import {
  NavigationItem,
  NavigationItemKey,
} from 'components/navigation/utils/libs';
import { isNavigationItemPermitted } from 'components/navigation/utils/isNavigationItemPermitted';
import MenuLink from './mainMenu/MenuLink';
import MainMenuCreateButton from 'components/navigation/verticalNavigation/mainMenu/MainMenuCreateButton';
import { useUser } from 'modules/user/userSelector';

const MAX_MAIN_MENU_ITEMS = 5;

type Props = {
  menuItems: NavigationItem[];
  activeItem?: NavigationItem;
};

const getMoreMenuItems = (items: NavigationItem[]): NavigationItem[] => {
  return items.slice(MAX_MAIN_MENU_ITEMS);
};

const getMainMenuItems = (items: NavigationItem[]): NavigationItem[] => {
  return items.slice(0, MAX_MAIN_MENU_ITEMS);
};

const AuthenticatedNavigationContent: FC<Props> = ({
  menuItems,
  activeItem,
}) => {
  const permissionsKeys = usePermissionsKeys();
  const user = useUser();

  const filteredMenuItems = useMemo(() => {
    const items = menuItems.filter((item) =>
      isNavigationItemPermitted(item, permissionsKeys),
    );

    if (!user.isClient) return items;

    const approvalsIndex = items.findIndex(
      (item) => item.key === NavigationItemKey.Approvals,
    );

    if (approvalsIndex <= 0) return items;

    const reordered = [...items];
    const [approvalsItem] = reordered.splice(approvalsIndex, 1);
    reordered.unshift(approvalsItem);
    return reordered;
  }, [menuItems, permissionsKeys, user.isClient]);

  const mainMenuItems = useMemo(
    () => getMainMenuItems(filteredMenuItems),
    [filteredMenuItems],
  );

  const moreMenuItems = useMemo(
    () => getMoreMenuItems(filteredMenuItems),
    [filteredMenuItems],
  );

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-y-4 tw-pt-4">
        <MainMenuCreateButton />
        {mainMenuItems.map((item, index) => (
          <MenuLink
            key={`${item.key}-${index}`}
            item={item}
            isActive={activeItem?.key === item.key}
          />
        ))}
      </div>
      {moreMenuItems.length > 0 && (
        <MoreMenuTooltip menuItems={moreMenuItems} activeItem={activeItem} />
      )}
    </>
  );
};

export default AuthenticatedNavigationContent;
